import { useState, useRef, useEffect } from 'react';
import { Check, CheckCheck, Copy, Trash2, Reply, Flag, MoreVertical, X, FileText, MapPin, User, Phone, Mail, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { getMediaUrl } from '@/utils/mediaUrl';
import VoicePlayer from './VoicePlayer';

const REACTIONS = [
  { key: 'namaste', emoji: '\u{1F64F}', label: 'Namaste' },
  { key: 'bless', emoji: '\u{2728}', label: 'Bless' },
  { key: 'heart', emoji: '\u{2764}\u{FE0F}', label: 'Love' },
  { key: 'om', emoji: '\u{1F549}\u{FE0F}', label: 'Om' },
];

export default function MessageBubble({ message, isMine, currentUserId, onDelete, onReport, onReact, onReply }) {
  const [showActions, setShowActions] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const actionsRef = useRef(null);
  const isDeleted = message.is_deleted_for_everyone;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setShowActions(false);
        setShowReactions(false);
      }
    };
    if (showActions || showReactions) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActions, showReactions]);

  const handleCopy = () => {
    if (message.text) {
      navigator.clipboard.writeText(message.text);
      toast.success('Copied');
    }
    setShowActions(false);
  };

  const handleLongPress = (e) => {
    e.preventDefault();
    if (!isDeleted) setShowActions(true);
  };

  const reactionsArray = message.reactions ? Object.entries(message.reactions) : [];
  const reactionCounts = {};
  reactionsArray.forEach(([uid, r]) => {
    reactionCounts[r] = (reactionCounts[r] || 0) + 1;
  });
  const myReaction = message.reactions?.[currentUserId];

  return (
    <div
      className={`flex ${isMine ? 'justify-end' : 'justify-start'} group relative`}
      data-testid={`message-${message.id}`}
      onContextMenu={handleLongPress}
    >
      <div className={`relative max-w-[80%] sm:max-w-[70%]`} ref={actionsRef}>
        {/* Reply context */}
        {message.reply_to && (
          <div className={`mb-0.5 px-3 py-1.5 rounded-t-xl text-xs border-l-3 ${
            isMine 
              ? 'bg-orange-600/20 border-l-orange-300 text-white/80' 
              : 'bg-slate-100 border-l-[#FF9933] text-slate-500'
          }`}>
            <p className="font-semibold text-[10px]">{message.reply_to.sender_name || 'Someone'}</p>
            <p className="truncate">{message.reply_to.text || '[Media]'}</p>
          </div>
        )}

        {/* Bubble */}
        <div
          className={`px-3 py-2 ${message.reply_to ? 'rounded-b-2xl' : 'rounded-2xl'} ${
            isMine
              ? `bg-gradient-to-br from-[#FF9933] to-[#E88A2D] text-white ${message.reply_to ? '' : 'rounded-br-md'} shadow-sm`
              : `bg-white text-slate-800 ${message.reply_to ? '' : 'rounded-bl-md'} shadow-sm border border-slate-100`
          }`}
          onClick={() => !isDeleted && setShowReactions(!showReactions)}
        >
          {isDeleted ? (
            <p className={`text-sm italic ${isMine ? 'text-white/60' : 'text-slate-400'}`}>
              This message was deleted
            </p>
          ) : (
            <>
              {/* Media */}
              {message.media_url && (
                <div className="mb-1.5">
                  {message.media_type === 'voice' ? (
                    <VoicePlayer
                      src={getMediaUrl(message.media_url)}
                      isMine={isMine}
                    />
                  ) : message.media_type === 'image' ? (
                    <img
                      src={getMediaUrl(message.media_url)}
                      alt=""
                      className="rounded-xl max-h-60 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={(e) => { e.stopPropagation(); window.open(getMediaUrl(message.media_url), '_blank'); }}
                    />
                  ) : message.media_type === 'document' ? (
                    <DocumentBubble message={message} isMine={isMine} />
                  ) : null}
                </div>
              )}

              {/* Location Message */}
              {message.media_type === 'location' && !message.media_url && (
                <LocationBubble message={message} isMine={isMine} />
              )}

              {/* Contact Message */}
              {message.media_type === 'contact' && !message.media_url && (
                <ContactBubble message={message} isMine={isMine} />
              )}

              {/* Text — hide for voice/location/contact messages since they have their own UI */}
              {message.text && !['voice', 'location', 'contact', 'document'].includes(message.media_type) && (
                <p className="text-[14.5px] leading-[1.35] whitespace-pre-wrap break-words">{message.text}</p>
              )}

              {/* Time & Status */}
              <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : 'justify-start'}`}>
                <span className={`text-[10px] ${isMine ? 'text-white/50' : 'text-slate-400'}`}>
                  {formatTime(message.created_at)}
                </span>
                {isMine && !isDeleted && (
                  message.status === 'seen'
                    ? <CheckCheck className="w-3.5 h-3.5 text-blue-200" />
                    : message.status === 'delivered'
                    ? <CheckCheck className="w-3.5 h-3.5 text-white/40" />
                    : <Check className="w-3.5 h-3.5 text-white/40" />
                )}
              </div>
            </>
          )}
        </div>

        {/* Reactions Display */}
        {Object.keys(reactionCounts).length > 0 && (
          <div className={`flex items-center gap-0.5 mt-[-6px] ${isMine ? 'justify-end mr-2' : 'justify-start ml-2'}`}>
            <div className="flex items-center gap-0.5 bg-white rounded-full px-1.5 py-0.5 shadow-sm border border-slate-100">
              {Object.entries(reactionCounts).map(([reaction, count]) => {
                const r = REACTIONS.find(r => r.key === reaction);
                return (
                  <span key={reaction} className={`text-xs cursor-pointer ${myReaction === reaction ? 'opacity-100' : 'opacity-70'}`}>
                    {r?.emoji || reaction}{count > 1 ? <span className="text-[9px] text-slate-500 ml-0.5">{count}</span> : ''}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Reaction Picker */}
        {showReactions && !isDeleted && (
          <div className={`absolute ${isMine ? 'right-0' : 'left-0'} bottom-full mb-1 z-50`}>
            <div className="flex items-center gap-1 bg-white rounded-full px-2 py-1.5 shadow-lg border border-slate-200">
              {REACTIONS.map((r) => (
                <button
                  key={r.key}
                  onClick={(e) => { e.stopPropagation(); onReact(message.id, r.key); setShowReactions(false); }}
                  className={`w-8 h-8 rounded-full flex items-center justify-center hover:bg-orange-50 transition-transform hover:scale-125 ${myReaction === r.key ? 'bg-orange-50 ring-2 ring-orange-300' : ''}`}
                  title={r.label}
                  data-testid={`react-${r.key}-${message.id}`}
                >
                  <span className="text-lg">{r.emoji}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Action Menu */}
        {showActions && !isDeleted && (
          <div className={`absolute ${isMine ? 'right-0' : 'left-0'} bottom-full mb-1 z-50 bg-white rounded-xl shadow-xl border border-slate-200 py-1 min-w-[160px]`}>
            <button onClick={() => { onReply(); setShowActions(false); }} className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-700 hover:bg-orange-50" data-testid={`reply-${message.id}`}>
              <Reply className="w-4 h-4 text-slate-400" /> Reply
            </button>
            {message.text && (
              <button onClick={handleCopy} className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-700 hover:bg-orange-50" data-testid={`copy-${message.id}`}>
                <Copy className="w-4 h-4 text-slate-400" /> Copy
              </button>
            )}
            {isMine && (
              <>
                <button onClick={() => { onDelete(message.id, false); setShowActions(false); }} className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-700 hover:bg-orange-50" data-testid={`delete-me-${message.id}`}>
                  <Trash2 className="w-4 h-4 text-slate-400" /> Delete for me
                </button>
                <button onClick={() => { onDelete(message.id, true); setShowActions(false); }} className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-red-600 hover:bg-red-50" data-testid={`delete-all-${message.id}`}>
                  <Trash2 className="w-4 h-4" /> Delete for everyone
                </button>
              </>
            )}
            {!isMine && (
              <button onClick={() => { onReport(message); setShowActions(false); }} className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-red-600 hover:bg-red-50" data-testid={`report-${message.id}`}>
                <Flag className="w-4 h-4" /> Report
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function DocumentBubble({ message, isMine }) {
  let docInfo = {};
  try { docInfo = JSON.parse(message.text); } catch {}
  const filename = docInfo.filename || 'Document';
  const size = docInfo.size || '';
  const ext = filename.split('.').pop()?.toUpperCase() || 'FILE';

  const extColors = {
    PDF: 'bg-red-500', DOC: 'bg-blue-500', DOCX: 'bg-blue-500',
    XLS: 'bg-green-600', XLSX: 'bg-green-600', PPT: 'bg-orange-600', PPTX: 'bg-orange-600',
    TXT: 'bg-slate-500', CSV: 'bg-teal-500', ZIP: 'bg-yellow-600',
  };

  return (
    <div
      className="flex items-center gap-3 cursor-pointer min-w-[180px]"
      onClick={(e) => { e.stopPropagation(); window.open(getMediaUrl(message.media_url), '_blank'); }}
      data-testid="document-bubble"
    >
      <div className={`w-10 h-10 rounded-lg ${extColors[ext] || 'bg-slate-500'} flex items-center justify-center flex-shrink-0`}>
        <FileText className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${isMine ? 'text-white' : 'text-slate-800'}`}>{filename}</p>
        <p className={`text-[10px] ${isMine ? 'text-white/60' : 'text-slate-400'}`}>{ext} {size && `- ${size}`}</p>
      </div>
      <Download className={`w-4 h-4 flex-shrink-0 ${isMine ? 'text-white/60' : 'text-slate-400'}`} />
    </div>
  );
}

function LocationBubble({ message, isMine }) {
  let loc = {};
  try { loc = JSON.parse(message.text); } catch {}
  const { lat, lng, name, address } = loc;

  if (!lat || !lng) return <p className={`text-sm ${isMine ? 'text-white/80' : 'text-slate-600'}`}>Location shared</p>;

  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.004},${lat - 0.002},${lng + 0.004},${lat + 0.002}&layer=mapnik&marker=${lat},${lng}`;
  const fullMapUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`;

  return (
    <div className="min-w-[200px] max-w-[260px]" data-testid="location-bubble">
      <div
        className="rounded-xl overflow-hidden h-28 mb-2 cursor-pointer border border-white/20"
        onClick={(e) => { e.stopPropagation(); window.open(fullMapUrl, '_blank'); }}
      >
        <iframe src={mapUrl} className="w-full h-full border-0 pointer-events-none" title="Location" />
      </div>
      <div className="flex items-start gap-2">
        <MapPin className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isMine ? 'text-white/70' : 'text-green-600'}`} />
        <div className="min-w-0">
          <p className={`text-sm font-medium ${isMine ? 'text-white' : 'text-slate-800'}`}>{name || 'Location'}</p>
          {address && (
            <p className={`text-[10px] line-clamp-2 ${isMine ? 'text-white/60' : 'text-slate-400'}`}>{address}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactBubble({ message, isMine }) {
  let contact = {};
  try { contact = JSON.parse(message.text); } catch {}
  const { name, username, phone, email, user_id } = contact;

  return (
    <div className="min-w-[180px] max-w-[240px]" data-testid="contact-bubble">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isMine ? 'bg-white/20' : 'bg-orange-100'}`}>
          <User className={`w-5 h-5 ${isMine ? 'text-white' : 'text-orange-600'}`} />
        </div>
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${isMine ? 'text-white' : 'text-slate-800'}`}>{name || 'Contact'}</p>
          {username && <p className={`text-[10px] ${isMine ? 'text-white/60' : 'text-slate-400'}`}>@{username}</p>}
        </div>
      </div>
      <div className={`space-y-1 pt-2 border-t ${isMine ? 'border-white/20' : 'border-slate-100'}`}>
        {phone && (
          <a
            href={`tel:${phone}`}
            className={`flex items-center gap-2 text-xs ${isMine ? 'text-white/80 hover:text-white' : 'text-blue-600 hover:text-blue-700'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Phone className="w-3.5 h-3.5" /> {phone}
          </a>
        )}
        {email && (
          <a
            href={`mailto:${email}`}
            className={`flex items-center gap-2 text-xs ${isMine ? 'text-white/80 hover:text-white' : 'text-blue-600 hover:text-blue-700'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Mail className="w-3.5 h-3.5" /> {email}
          </a>
        )}
        {user_id && (
          <a
            href={`/profile/${user_id}`}
            className={`flex items-center gap-2 text-xs font-medium ${isMine ? 'text-white/80 hover:text-white' : 'text-orange-600 hover:text-orange-700'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="w-3.5 h-3.5" /> View Profile
          </a>
        )}
      </div>
    </div>
  );
}
